import { pathOr } from 'ramda';
import { delay, eventChannel } from 'redux-saga';
import {
  all,
  call,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
  cancelled,
} from 'redux-saga/effects';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';
import {
  types as cadViewerTypes,
  measurementTool,
  selectorTool,
  viewerState,
} from 'modules/common/viewer/cadViewer/data/actions';
import {
  getCaliperToolSelections,
  getCapturedCaliperCADState,
  getCapturedCaliperState,
  getCurrentMeasurementValue,
  getEncodedCADState,
  getInterrogationInfo,
  getSelectedFeatures,
  getSelectedManufacturabilityFeedback,
  getSelectorToolSelections,
} from 'modules/common/viewer/cadViewer/data/reducer';
import {
  viewerAnnotations,
  viewerMeasurements,
} from 'modules/common/viewer/pdfViewer/data/actions';
import {
  getViewerAnnotations,
  getViewerMeasurements,
} from 'modules/common/viewer/pdfViewer/data/reducer';
import { QUOTE_APPS_PANEL_STATES } from 'modules/quotes/quoteItems/apps/data/constants';
import { getQuoteAppMetaForType } from 'modules/quotes/quoteItems/apps/data/reducer';
import { partLibraryViewerPage } from 'modules/viewerPage/data/actions';
import { APPS_PANEL_STATES } from 'modules/viewerPage/data/constants';
import {
  getActivePartFile,
  getActivePartUUID,
} from 'modules/viewerPage/data/reducer';

import notificationService from 'services/notificationService';
import partLibraryService from 'services/partLibraryService';
import socketSingleton from 'services/webSocketService';

import { PAGE_REQUEST } from 'store/app/actions';
import { getHomePage, getLocation } from 'store/app/selectors';
import { getFeatureFlagValue } from 'store/session/selectors';
import { getTeamMembers } from 'store/supplier/sagas';

import history from 'utils/browserHistory';

import { entitiesStore } from '../entities/actions';
import {
  getAnnotationById,
  getChannelById,
  getInternalTeamChatChannelId,
  getReqsReviewChatChannelId,
  getMSCChatChannelId,
  getNormalizedChannelById,
} from '../entities/reducer';
import { taskAssignment } from '../tasks/data/actions';
import { getTaskAssignmentInfo } from '../tasks/data/sagas';
import {
  annotationTool,
  channelsList,
  chatDrawer,
  chatDrawerState,
  chatSocketInitialize,
  createAnnotation,
  createComment,
  deleteAnnotation,
  deleteComment,
  editComment,
  editMscChannelConfig,
  fetchSuppliersMscSettings,
  reinitializeTrustedThirdPartyChannel,
  threads,
  types,
  chatApp,
} from './actions';
import {
  ANNOTATION_TOOL_TYPES,
  CHAT_DRAWER_STATE,
  CHANNEL_TYPES,
  PAGE_SOURCE,
} from './constants';
import {
  getActiveChannelId,
  getAnnotationToolState,
  getIsOwner,
  getPartMasterVersion,
  getSource,
  getTechDataPackageRootUuid,
} from './reducer';

export default function* root() {
  yield takeEvery(
    types.ACKNOWLEDGE_CHANNEL_NOTIFICATIONS_REQUEST,
    watchAcknowledgeChannelNotifications,
  );
  yield takeEvery(
    types.ACKNOWLEDGE_THREAD_NOTIFICATIONS_REQUEST,
    watchAcknowledgeThreadNotifications,
  );
  yield takeLatest(types.CREATE_ANNOTATION_REQUEST, watchCreateAnnotation);
  yield takeLatest(types.CREATE_COMMENT_REQUEST, watchCreateComment);
  yield takeLatest(types.CHAT_APP_INIT, watchInitChatApp);
  yield takeLatest(types.CHAT_PANEL_INIT, watchInitChatPanel);
  yield takeLatest(types.CHAT_SOCKET_INITIALIZE_REQUEST, connectToChatSocket);
  yield takeEvery(types.DELETE_ANNOTATION_REQUEST, watchDeleteAnnotation);
  yield takeEvery(types.DELETE_COMMENT_REQUEST, watchDeleteComment);
  yield takeEvery(types.EDIT_COMMENT_REQUEST, watchEditComment);
  yield takeEvery(
    types.EDIT_MSC_CHANNEL_CONFIG_REQUEST,
    watchEditMscChannelConfig,
  );
  yield takeLatest(
    types.FETCH_SUPPLIERS_MSC_SETTINGS_REQUEST,
    watchFetchSuppliersMscSettings,
  );
  yield takeLatest(
    types.REINITIALIZE_TRUSTED_THIRD_PARTY_CHANNEL_REQUEST,
    watchReinitializeTrustedThirdPartyChat,
  );
  yield takeLatest(PAGE_REQUEST, watchPageRequest);
}

function* watchPageRequest(action) {
  const { location } = action.payload;
  const prevLocation = yield select(getLocation);

  if (
    !location.pathname.includes('/quotes/edit') ||
    !prevLocation?.pathname.includes('/quotes/edit')
  ) {
    yield put(chatApp.reset());
  }
}

function* createAnnotationForBaq(
  comment,
  channelId,
  createNewChannel,
  actionUuid,
  commentedOnQuoteItemId,
  channelType,
  passedPartMasterVersion,
) {
  // handle task assignment
  let task;
  if (channelType === CHANNEL_TYPES.PRIMARY) {
    task = yield call(getTaskAssignmentInfo);
  }

  // Get quote item ID from chat panel meta (new system) or use passed parameter
  const chatMeta = yield select(
    getQuoteAppMetaForType,
    QUOTE_APPS_PANEL_STATES.CHAT,
  );
  const finalCommentedOnQuoteItemId =
    commentedOnQuoteItemId || chatMeta?.quoteItemId || null;

  // Use passed partMasterVersion with fallback to selector
  const selectorPartMasterVersion = yield select(getPartMasterVersion);
  const partMasterVersion =
    passedPartMasterVersion || selectorPartMasterVersion || 1;

  const annotation = {
    initialComment: comment,
    partMasterVersion,
    ...(task && { task }),
    ...(finalCommentedOnQuoteItemId && {
      commentedOnQuoteItemId: finalCommentedOnQuoteItemId,
    }),
    ...(actionUuid && { actionUuid }),
  };

  const response = yield call(
    partLibraryService.annotations.create,
    channelId,
    annotation,
  );

  if (task) {
    // clear tasks
    yield put(taskAssignment.reset());
  }
  return response;
}

function* createAnnotationForViewer(
  comment,
  channelId,
  createdNewChannel,
  actionUuid,
  commentedOnQuoteItemId,
  channelType,
  passedPartMasterVersion,
) {
  // handle task assignment
  let task;
  if (channelType === CHANNEL_TYPES.PRIMARY) {
    task = yield call(getTaskAssignmentInfo);
  }

  const activeFile = yield select(getActivePartFile);

  const isActiveFile3DRenderable = activeFile?.hasGeometry;

  let currentViewerState;
  if (isActiveFile3DRenderable) {
    // get the current viewer state
    yield put(viewerState.captureNextStateRequest());
    // take current viewer state or timeout after 1250ms
    const { viewerStateAction } = yield race({
      timeout: call(delay, 1250),
      viewerStateAction: take(cadViewerTypes.VIEWER_STATE_RECEIVE),
    });
    if (viewerStateAction) {
      currentViewerState = viewerStateAction.payload.viewerState;
    }
  }

  const interrogationInfo = yield select(getInterrogationInfo);

  // get part annotations
  let selectedObjects = [];
  let caliperObjects = [];
  let pdfAnnotations = [];
  let pdfMeasurements = [];
  let capturedCaliperState = {};
  let capturedCaliperCADState = {};
  let currentMeasurementValue = null;
  let featureIds = null;
  let feedbackItemIds = null;

  const annotationToolState = yield select(getAnnotationToolState);

  const activeToolForNewChannel =
    createdNewChannel && annotationToolState.channelType === channelType;

  const activeToolForExistingChannel =
    !createdNewChannel &&
    annotationToolState.channelId === channelId &&
    annotationToolState.channelType === channelType;

  if (activeToolForExistingChannel || activeToolForNewChannel) {
    if (annotationToolState.tool === ANNOTATION_TOOL_TYPES.MEASUREMENT_TOOL) {
      caliperObjects = yield select(getCaliperToolSelections);
      capturedCaliperState = yield select(getCapturedCaliperState);
      capturedCaliperCADState = yield select(getCapturedCaliperCADState);
      currentMeasurementValue = yield select(getCurrentMeasurementValue);
    } else if (
      annotationToolState.tool === ANNOTATION_TOOL_TYPES.SELECTOR_TOOL
    ) {
      selectedObjects = yield select(getSelectorToolSelections);
    } else if (
      annotationToolState.tool === ANNOTATION_TOOL_TYPES.PDF_HIGHLIGHT_TOOL
    ) {
      pdfAnnotations = yield select(getViewerAnnotations);
      pdfMeasurements = yield select(getViewerMeasurements);
    } else if (
      annotationToolState.tool === ANNOTATION_TOOL_TYPES.FEATURE_TOOL
    ) {
      const allFeatures = yield select(getSelectedFeatures);
      const selectedFeatures = allFeatures.filter((f) => f.canTag);
      const allFeedback = yield select(getSelectedManufacturabilityFeedback);
      const selectedFeedback = allFeedback.filter((f) => f.canTag);
      const shouldConstructFeatures =
        interrogationInfo &&
        interrogationInfo.interrogationFileId &&
        (selectedFeatures.length > 0 || selectedFeedback.length > 0);
      if (shouldConstructFeatures) {
        featureIds = selectedFeatures.map((f) => f.id);
        feedbackItemIds = selectedFeedback.map((f) => f.id);
      }
    }
  }

  const encodedCadViewerState = yield select(getEncodedCADState);

  // Use passed partMasterVersion with fallback to selector
  const selectorPartMasterVersion = yield select(getPartMasterVersion);
  const partMasterVersion =
    passedPartMasterVersion || selectorPartMasterVersion || 1;

  // format request
  const annotation = {
    cadViewerState: JSON.stringify(encodedCadViewerState),
    partInterrogationFileId:
      (interrogationInfo || {}).interrogationFileId || null,
    featureIds,
    feedbackItemIds,
    measurement:
      currentMeasurementValue != null
        ? {
            caliperObjects,
            distance: currentMeasurementValue,
            viewerState: JSON.stringify(capturedCaliperState),
            cadViewerState: JSON.stringify(capturedCaliperCADState),
          }
        : null,
    initialComment: comment,
    partFile: activeFile?.uuid,
    partMasterVersion,
    pdfAnnotations: pdfAnnotations,
    pdfMeasurements: pdfMeasurements,
    resolved: false,
    selection:
      selectedObjects && selectedObjects.length
        ? selectedObjects.map((selectedObject) => ({
            ...selectedObject,
            viewerState: JSON.stringify(selectedObject.viewerState),
            cadViewerState: JSON.stringify(selectedObject.cadViewerState),
          }))
        : null,
    task,
    viewerState: JSON.stringify(currentViewerState),
    // ACTIONS channel specific fields
    ...(actionUuid && { actionUuid }),
    ...(commentedOnQuoteItemId && { commentedOnQuoteItemId }),
  };

  const response = yield call(
    partLibraryService.annotations.create,
    channelId,
    annotation,
  );

  if (activeToolForExistingChannel || activeToolForNewChannel) {
    // clear selections and tools
    yield put(selectorTool.disable());
    yield put(measurementTool.disable());
    yield put(annotationTool.setInactive());
    yield put(viewerAnnotations.removeAll());
    yield put(viewerMeasurements.removeAll());
  }

  if (task) {
    // clear tasks
    yield put(taskAssignment.reset());
  }

  return response;
}

function* setActiveChannelStateForBaq({ channel }) {
  yield put(
    chatDrawerState.set({
      activeChannelId: channel.id,
      activeChannelType: channel.type,
      chatDrawerState:
        channel.type === CHANNEL_TYPES.PRIMARY
          ? CHAT_DRAWER_STATE.TEAM
          : channel.type === CHANNEL_TYPES.ACTIONS
          ? CHAT_DRAWER_STATE.REQS_REVIEW
          : CHAT_DRAWER_STATE.EXTERNAL,
    }),
  );
}

function* setActiveChannelStateForViewer({ channel }) {
  const isThirdPartyChannel =
    channel.type === CHANNEL_TYPES.MSC || channel.type === CHANNEL_TYPES.PENN;
  // if not a third party channel, set the apps panel state before setting the active channel so that the url params can be used
  if (!isThirdPartyChannel) {
    // Check if there's already a URL-specified app panel state to avoid overriding it
    const currentPanelState = yield select(
      (state) => state.modules?.viewerPage?.appsPanelState,
    );

    // Only set to VIEWER_CHAT if not an actions channel, or if already on VIEWER_CHAT
    if (
      channel.type !== CHANNEL_TYPES.ACTIONS ||
      currentPanelState === APPS_PANEL_STATES.VIEWER_CHAT
    ) {
      yield put(
        partLibraryViewerPage.setAppsPanelState({
          appsPanelState: APPS_PANEL_STATES.VIEWER_CHAT,
        }),
      );
    }
  }
  // set the active channel
  yield put(
    chatDrawerState.set({
      activeChannelId: channel.id,
      activeChannelType: channel.type,
      chatDrawerState:
        channel.type === CHANNEL_TYPES.SECONDARY
          ? CHAT_DRAWER_STATE.EXTERNAL
          : channel.type === CHANNEL_TYPES.ACTIONS
          ? CHAT_DRAWER_STATE.REQS_REVIEW
          : CHAT_DRAWER_STATE.TEAM,
    }),
  );
  // open the correct app
  if (isThirdPartyChannel) {
    if (channel.type === CHANNEL_TYPES.MSC) {
      yield put(
        partLibraryViewerPage.setAppsPanelState({
          appsPanelState: APPS_PANEL_STATES.MSC_CHAT,
        }),
      );
    } else if (channel.type === CHANNEL_TYPES.PENN) {
      yield put(
        partLibraryViewerPage.setAppsPanelState({
          appsPanelState: APPS_PANEL_STATES.PENN_CHAT,
        }),
      );
    }
  }
}

function createSocketChannel(isOwner, partMasterUuid, channelId) {
  // `eventChannel` takes a subscriber function
  // the subscriber function takes an `emit` argument to put messages onto the channel
  return eventChannel((emit) => {
    if (isOwner) {
      const unsubscribeChannel = socketSingleton.subscribe(
        `chat.part.${partMasterUuid}`,
        emit,
      );

      return unsubscribeChannel.unsubscribe;
    } else {
      const unsubscribeChannel = socketSingleton.subscribe(
        `chat.channel.${channelId}`,
        emit,
      );

      return unsubscribeChannel.unsubscribe;
    }
  });
}

function* watchAcknowledgeChannelNotifications(action) {
  try {
    const { channelId } = action.payload;
    yield notificationService.chat.acknowledgeChannelNotifications(channelId);
  } catch (e) {
    console.error('Failed to acknowledge channel notifications', e);
  }
}

function* watchAcknowledgeThreadNotifications(action) {
  try {
    const { annotationId } = action.payload;
    yield notificationService.chat.acknowledgeThreadNotifications(annotationId);
  } catch (e) {
    console.error('Failed to acknowledge thread notifications', e);
  }
}

function* watchChatSocketMessages() {
  const partMasterUuid = yield select(getTechDataPackageRootUuid);
  const isOwner = yield select(getIsOwner);
  const channelId = yield select(getActiveChannelId);

  const socketChannel = yield call(
    createSocketChannel,
    isOwner,
    partMasterUuid,
    channelId,
  );

  try {
    while (true) {
      // An error from socketChannel will cause the saga to jump to the catch block
      const message = yield take(socketChannel);

      const annotation = pathOr(null, ['annotation'], message);
      const channel = pathOr(null, ['channel'], message);
      const comment = pathOr(null, ['comment'], message);
      const share = pathOr(null, ['share'], message);
      const channelEvent = pathOr(null, ['channelEvent'], message);
      const task = pathOr(null, ['task'], message);
      if (annotation) {
        yield put(entitiesStore.receiveAnnotation(annotation));
        const activeChannelId = yield select(getActiveChannelId);
        if (!activeChannelId) {
          const channelId = annotation.channelId;
          const channel = yield select(getNormalizedChannelById, channelId);
          if (channel) {
            yield put(
              chatDrawerState.set({
                activeChannelId: channelId,
                activeChannelType: channel.type,
                chatDrawerState:
                  channel.type === CHANNEL_TYPES.MSC ||
                  channel.type === CHANNEL_TYPES.SECONDARY
                    ? CHAT_DRAWER_STATE.EXTERNAL
                    : channel.type === CHANNEL_TYPES.ACTIONS
                    ? CHAT_DRAWER_STATE.REQS_REVIEW
                    : CHAT_DRAWER_STATE.TEAM,
              }),
            );
          }
        }
      } else if (channel) {
        yield put(entitiesStore.receiveChannels([channel]));
      } else if (comment) {
        yield put(entitiesStore.receiveComment(comment));
      } else if (channelEvent) {
        yield put(entitiesStore.receiveChannelEvent(channelEvent));
      } else if (share) {
        yield put(entitiesStore.receiveShare(share));
        const isOwner = yield select(getIsOwner);
        if (!isOwner) {
          // check if shared with user still has access
          try {
            const partUUID = yield select(getActivePartUUID);
            yield call(partLibraryService.sharedTechDataPackage.get, partUUID);
          } catch (error) {
            const statusCode = pathOr(null, ['statusCode'], error);
            if (statusCode == 403) {
              // redirect unauthorized user from page
              window.alert('You no longer have permission to view this part.');
              history.push(yield select(getHomePage));
            } else {
              throw error;
            }
          }
        }
      } else if (task) {
        yield put(entitiesStore.receiveTask(task));
      } else {
        console.error(
          'ERROR: RECEIVED A SOCKET MESSAGE OF UNKNOWN FORMAT',
          message,
        );
      }
    }
  } catch (err) {
    console.error('Received a socket error, terminating the channel');
    // If we get an error on the socket connection, or if the server
    // disconnects, we want to close the channel and terminate this saga

    // We want to open a new connection to the socket server before closing
    // this one to avoid missing any messages
    yield put(chatSocketInitialize.request());
    // The socketChannel is still open in the catch block
    // If we want to end the socketChannel, we need to close it explicitly
    socketChannel.close();
  } finally {
    // If the saga is cancelled (likely in the event that the user leaves
    // the page), manually kill the socket connection so
    // the server can deallocate resources
    if (yield cancelled()) {
      socketChannel.close();
    }
  }
}

export function* connectToChatSocket() {
  // Send a request to initialize a chat queue and receive a socket token
  yield race({
    chatSocket: call(watchChatSocketMessages),
    otherPage: take(PAGE_REQUEST),
  });
}

function* watchCreateAnnotation(action) {
  try {
    const {
      channelType,
      comment,
      actionUuid,
      partMasterUuid,
      commentedOnQuoteItemId,
      partMasterVersion,
    } = action.payload;
    let { channelId } = action.payload;
    const createNewChannel = !channelId;

    const [source, techDataPackageRootUuid] = yield all([
      select(getSource),
      select(getTechDataPackageRootUuid),
    ]);

    // Use techDataPackageRootUuid for viewer context, fallback to partMasterUuid for BAQ context
    const finalPartUuid = techDataPackageRootUuid || partMasterUuid;

    if (createNewChannel) {
      // create a channel if none exist
      // Use passed partMasterVersion with fallback to selector
      const selectorPartMasterVersion = yield select(getPartMasterVersion);
      const finalPartMasterVersion =
        partMasterVersion || selectorPartMasterVersion || 1;
      const newChannelResponse = yield partLibraryService.channels.create(
        finalPartUuid,
        {
          partMasterVersion: finalPartMasterVersion,
          type: channelType,
        },
      );
      channelId = newChannelResponse.id;
      yield put(createAnnotation.recordAnnotationCreating({ channelId }));

      yield put(entitiesStore.receiveChannels([newChannelResponse]));

      if (source === PAGE_SOURCE.BAQ || source === PAGE_SOURCE.REQS_REVIEW) {
        yield call(setActiveChannelStateForBaq, {
          channel: newChannelResponse,
        });
      } else if (source === PAGE_SOURCE.VIEWER) {
        yield call(setActiveChannelStateForViewer, {
          channel: newChannelResponse,
        });
      }
    }

    let response;
    switch (source) {
      case PAGE_SOURCE.BAQ: {
        response = yield call(
          createAnnotationForBaq,
          comment,
          channelId,
          createNewChannel,
          actionUuid,
          commentedOnQuoteItemId,
          channelType,
          partMasterVersion,
        );
        break;
      }
      case PAGE_SOURCE.VIEWER:
      default: {
        response = yield call(
          createAnnotationForViewer,
          comment,
          channelId,
          createNewChannel,
          actionUuid,
          commentedOnQuoteItemId,
          channelType,
          partMasterVersion,
        );
      }
    }

    yield put(entitiesStore.receiveAnnotation(response));
    yield put(createAnnotation.success({ channelId, channelType }));
  } catch (e) {
    yield put(
      createAnnotation.error({
        channelId: pathOr(null, ['payload', 'channelId'], action),
        error: e,
      }),
    );
  }
}

function* watchCreateComment(action) {
  try {
    const { annotationId, comment } = action.payload;

    const response = yield call(
      partLibraryService.comments.create,
      annotationId,
      { content: comment, parentId: null },
    );

    yield put(entitiesStore.receiveComment(response));
    yield put(createComment.success({ annotationId }));
  } catch (e) {
    yield put(
      createComment.error(e, pathOr(null, ['payload', 'annotationId'], action)),
    );
  }
}

function* watchDeleteAnnotation(action) {
  try {
    const { annotationId } = action.payload;
    yield call(partLibraryService.annotations.delete, annotationId);
    yield put(deleteAnnotation.success());
  } catch (error) {
    yield put(deleteAnnotation.error(error));
  }
}

function* watchDeleteComment(action) {
  try {
    const { commentId } = action.payload;
    yield call(partLibraryService.comments.delete, commentId);
    yield put(deleteComment.success());
  } catch (error) {
    yield put(deleteComment.error(error));
  }
}

function* watchEditComment(action) {
  try {
    const { commentId, content } = action.payload;
    const response = yield call(partLibraryService.comments.patch, commentId, {
      content,
    });

    yield put(entitiesStore.receiveComment(response));
    yield put(editComment.success({ commentId }));
  } catch (error) {
    const commentId = pathOr(null, ['payload', 'commentId'], action);
    yield put(editComment.error(error, commentId));
  }
}

function* watchEditMscChannelConfig(action) {
  try {
    const { mscChannelId, mscSpecialistId } = action.payload;
    const channel = yield call(
      partLibraryService.mscChannelConfig.patch,
      mscChannelId,
      { mscSpecialistId },
    );
    yield put(entitiesStore.receiveChannel(channel));
    yield put(editMscChannelConfig.success());
  } catch (error) {
    yield put(editMscChannelConfig.error(error));
  }
}

function* watchFetchChannels() {
  try {
    const techDataPackageRootUuid = yield select(getTechDataPackageRootUuid);
    const results = yield call(
      partLibraryService.channels.list,
      techDataPackageRootUuid,
    );

    yield put(channelsList.success());
    yield put(entitiesStore.receiveChannels(results));
  } catch (e) {
    console.error('Error in watchFetchChannels', e);
    yield put(channelsList.error());
  }
}

function* setInitialChatStateForBaq({ annotationId, channelId }) {
  // open to specified channel/annotation
  if (annotationId) {
    // verify annotation exists
    const annotation = yield select(getAnnotationById, annotationId);
    if (annotation) {
      const channel = yield select(
        getNormalizedChannelById,
        annotation.channelId,
      );
      if (channel) {
        // set the active channel
        yield call(setActiveChannelStateForBaq, { channel });
        // only show annotation if it is not soft deleted
        if (annotation.isLive) {
          yield put(
            threads.setActive({
              annotationId,
              channelId: annotation.channelId,
            }),
          );
        }
        yield put(chatDrawer.open());
      }
    }
  } else if (channelId) {
    const channel = yield select(getNormalizedChannelById, channelId);
    // verify channel exists
    if (channel) {
      yield call(setActiveChannelStateForBaq, { channel });
      yield put(chatDrawer.open());
    }
  }
}

function* setInitialChatStateForViewer({ annotationId, channelId }) {
  // open to specified channel/annotation
  if (annotationId) {
    // verify annotation exists
    const annotation = yield select(getAnnotationById, annotationId);
    if (annotation) {
      const channel = yield select(
        getNormalizedChannelById,
        annotation.channelId,
      );
      if (channel) {
        yield call(setActiveChannelStateForViewer, { channel });
        // only show annotation if it is not soft deleted
        if (annotation.isLive) {
          yield put(
            threads.setActive({
              annotationId,
              channelId: annotation.channelId,
            }),
          );
        }
        yield put(chatDrawer.open());
      }
    }
  } else if (channelId) {
    const channel = yield select(getNormalizedChannelById, channelId);
    // verify channel exists
    if (channel) {
      yield call(setActiveChannelStateForViewer, { channel });
      yield put(chatDrawer.open());
    }
  }
}

function* watchInitChatApp(action) {
  yield put(entitiesStore.init());
  const { payload } = action;

  yield all([call(watchFetchChannels), call(getTeamMembers)]);

  const isMscChatAvailable = yield select(
    getFeatureFlagValue,
    featureFlagKeys.isMscChatAvailable,
  );
  if (isMscChatAvailable) {
    yield put(fetchSuppliersMscSettings.request());
  }

  // handle url params
  const { annotationId, channelId, source, activeChannelType } = payload || {};

  if (annotationId || channelId) {
    // handle opening towards specified annotation or channel
    if (source === PAGE_SOURCE.BAQ) {
      yield call(setInitialChatStateForBaq, { annotationId, channelId });
    } else if (source === PAGE_SOURCE.VIEWER) {
      yield call(setInitialChatStateForViewer, { annotationId, channelId });
    }
  } else {
    const isOwner = yield select(getIsOwner);
    if (activeChannelType == 'msc') {
      const mscChannelId = yield select(getMSCChatChannelId);
      yield put(
        chatDrawerState.set({
          activeChannelId: mscChannelId,
          activeChannelType: CHANNEL_TYPES.MSC,
          chatDrawerState: CHAT_DRAWER_STATE.EXTERNAL,
        }),
      );
    } else if (isOwner) {
      if (source === PAGE_SOURCE.REQS_REVIEW) {
        const reqsReviewChatChannelId = yield select(
          getReqsReviewChatChannelId,
        );
        yield put(
          chatDrawerState.set({
            activeChannelId: reqsReviewChatChannelId,
            activeChannelType: CHANNEL_TYPES.ACTIONS,
            chatDrawerState: CHAT_DRAWER_STATE.REQS_REVIEW,
          }),
        );
      } else {
        const teamChannelId = yield select(getInternalTeamChatChannelId);
        yield put(
          chatDrawerState.set({
            activeChannelId: teamChannelId,
            activeChannelType: CHANNEL_TYPES.PRIMARY,
            chatDrawerState: CHAT_DRAWER_STATE.TEAM,
          }),
        );
      }
    } else {
      yield put(
        chatDrawerState.set({
          activeChannelId: null,
          activeChannelType: null,
          chatDrawerState: CHAT_DRAWER_STATE.EXTERNAL,
        }),
      );
    }
  }

  yield put(chatSocketInitialize.request());
}

function* watchFetchSuppliersMscSettings() {
  try {
    const response = yield call(partLibraryService.suppliersMscSettings.get);
    yield put(
      fetchSuppliersMscSettings.success({ suppliersMscSettings: response }),
    );
  } catch (error) {
    console.error('Failed to fetch suppliers MSC settings', error);
    yield put(fetchSuppliersMscSettings.error(error));
  }
}

function* watchInitChatPanel(action) {
  const { payload } = action;
  const { source } = payload;
  // Fetch all data in parallel (same as chat drawer)
  yield all([call(watchFetchChannels), call(getTeamMembers)]);

  // Initialize socket connection
  yield put(chatSocketInitialize.request());

  // Fetch MSC settings if available
  const isMscChatAvailable = yield select(
    getFeatureFlagValue,
    featureFlagKeys.isMscChatAvailable,
  );
  if (isMscChatAvailable) {
    yield put(fetchSuppliersMscSettings.request());
  }

  // Set initial chat state to clear loading state (same logic as drawer)
  const isOwner = yield select(getIsOwner);
  if (isOwner) {
    if (source === PAGE_SOURCE.REQS_REVIEW) {
      const reqsReviewChatChannelId = yield select(getReqsReviewChatChannelId);
      yield put(
        chatDrawerState.set({
          activeChannelId: reqsReviewChatChannelId,
          activeChannelType: CHANNEL_TYPES.ACTIONS,
          chatDrawerState: CHAT_DRAWER_STATE.REQS_REVIEW,
        }),
      );
    } else {
      const teamChannelId = yield select(getInternalTeamChatChannelId);
      yield put(
        chatDrawerState.set({
          activeChannelId: teamChannelId,
          activeChannelType: CHANNEL_TYPES.PRIMARY,
          chatDrawerState: CHAT_DRAWER_STATE.TEAM,
        }),
      );
    }
  } else {
    yield put(
      chatDrawerState.set({
        activeChannelId: null,
        activeChannelType: null,
        chatDrawerState: CHAT_DRAWER_STATE.EXTERNAL,
      }),
    );
  }
}

function* watchReinitializeTrustedThirdPartyChat() {
  try {
    const channelId = yield select(getActiveChannelId);
    const channel = yield select(getChannelById, channelId);
    const partMasterVersion = yield select(getPartMasterVersion);
    if (
      channel.type !== CHANNEL_TYPES.MSC &&
      channel.type !== CHANNEL_TYPES.PENN
    ) {
      console.error(
        'ATTEMPTED OT REINITIALIZE NON-TRUSTED THIRD PARTY CHANNEL',
      );
      throw new Error('Invalid channel.');
    }
    const lastShare = pathOr(null, ['shares', 0], channel);
    yield partLibraryService.shares.create(channelId, {
      emailAddress: lastShare.sharedWithEmail,
      emailSubjectLine: lastShare.emailSubjectLine,
      message: null,
      partMasterVersion,
    });
    yield put(reinitializeTrustedThirdPartyChannel.success());
  } catch (error) {
    yield put(reinitializeTrustedThirdPartyChannel.error(error));
  }
}
